import { Typography } from '@/components/atoms';
import type { TypographyVariant } from '@/components/atoms/Typography';
import { GUEST_PROJECTS_NEXT_PAGE_SKELETON_COUNT } from '@/core/api/services/guestProjectsService';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import type { GuestPropertyCardModel } from '../types/guest.types';
import { GuestListEmptyState } from './GuestListEmptyState';
import { GuestPropertyCard } from './GuestPropertyCard';
import { GuestPropertyCardSkeleton } from './skeleton';

const NEXT_PAGE_SKELETON_KEYS = Array.from(
  { length: GUEST_PROJECTS_NEXT_PAGE_SKELETON_COUNT },
  (_, index) => `guest-explore-next-skeleton-${index}`,
);

export interface GuestExploreListProps {
  readonly properties: ReadonlyArray<GuestPropertyCardModel>;
  readonly emptyTitle: string;
  readonly exploreTitle: string;
  readonly titleVariant: TypographyVariant;
  readonly showTitle: boolean;
  readonly isFetchingNextPage?: boolean;
  readonly onPropertyPress: (id: string) => void;
}

/** Explore sidebar / tab list for guest unit details. */
export function GuestExploreList({
  properties,
  emptyTitle,
  exploreTitle,
  titleVariant,
  showTitle,
  isFetchingNextPage = false,
  onPropertyPress,
}: Readonly<GuestExploreListProps>) {
  const showEmpty = properties.length === 0 && !isFetchingNextPage;

  return (
    <View style={styles.root}>
      {showTitle ? (
        <Typography
          variant={titleVariant}
          color="primary"
          style={styles.exploreTitle}
        >
          {exploreTitle}
        </Typography>
      ) : null}
      {showEmpty ? <GuestListEmptyState title={emptyTitle} /> : null}
      {properties.map((property) => (
        <GuestPropertyCard
          key={property.id}
          property={property}
          onPress={() => onPropertyPress(property.id)}
          fullWidth
          disableAutoScroll
        />
      ))}
      {isFetchingNextPage
        ? NEXT_PAGE_SKELETON_KEYS.map((skeletonKey) => (
            <GuestPropertyCardSkeleton key={skeletonKey} fullWidth />
          ))
        : null}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    gap: 16,
    width: '100%',
    minWidth: 0,
  },
  exploreTitle: {
    letterSpacing: 0,
  },
});

export default GuestExploreList;
